import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import styled from 'styled-components';

import { tagToneColors } from 'components/constants/tagTones';
import { useMailboxAccess, useMailErrorText } from 'core/hooks';
import { T } from 'services/ThemeTokens';

interface IMailboxAccessBlockProps {
  userId: string;
}

/**
 * ТЗ 0122 — «Почтовые ящики» сотрудника (только Admin): какие рабочие ящики
 * он видит в почте и может ли из них отправлять. Личный ящик сотрудника —
 * доступ без строки, его не снять. Каждая галочка сохраняется сразу.
 */
export const MailboxAccessBlock = memo(
  ({ userId }: IMailboxAccessBlockProps) => {
    const { t } = useTranslation();
    const errorText = useMailErrorText();
    const { mailboxes, loading, failed, busyId, setAccess } = useMailboxAccess(
      userId,
    );
    const [status, setStatus] = useState<{
      text: string;
      error: boolean;
    } | null>(null);

    const change = async (
      mailboxId: string,
      granted: boolean,
      canSend: boolean,
    ) => {
      setStatus(null);
      const result = await setAccess(mailboxId, granted, canSend);
      setStatus(
        result.ok
          ? { text: t('mail.access.saved'), error: false }
          : { text: errorText(result), error: true },
      );
    };

    return (
      <Wrap aria-labelledby={`mail-access-${userId}`}>
        <Title id={`mail-access-${userId}`}>
          <Mail size={14} strokeWidth={2} aria-hidden />
          {t('mail.access.title')}
        </Title>
        <Hint>{t('mail.access.hint')}</Hint>
        {loading ? (
          <Hint role="status">{t('mail.list.loading')}</Hint>
        ) : failed ? (
          <Notice role="alert">{t('mail.access.failed')}</Notice>
        ) : mailboxes.length === 0 ? (
          <Hint>{t('mail.access.none')}</Hint>
        ) : (
          <List>
            {mailboxes.map((box) => {
              const busy = busyId === box.mailboxId;
              return (
                <Item key={box.mailboxId}>
                  <Check>
                    <input
                      type="checkbox"
                      checked={box.granted}
                      disabled={box.implicit || busy}
                      title={
                        box.implicit ? t('mail.access.ownHint') : undefined
                      }
                      onChange={(event) =>
                        change(box.mailboxId, event.target.checked, box.canSend)
                      }
                    />
                    <span>
                      {box.displayName ? `${box.displayName} · ` : ''}
                      {box.email}
                    </span>
                    <Tag $tone={box.implicit ? 'info' : 'neutral'}>
                      {box.implicit
                        ? t('mail.access.own')
                        : t(`mail.access.kinds.${box.kind}`, {
                            defaultValue: box.kind,
                          })}
                    </Tag>
                  </Check>
                  <Check $muted={!box.granted}>
                    <input
                      type="checkbox"
                      checked={box.granted && box.canSend}
                      disabled={!box.granted || box.implicit || busy}
                      onChange={(event) =>
                        change(box.mailboxId, true, event.target.checked)
                      }
                    />
                    <span>{t('mail.access.canSend')}</span>
                  </Check>
                </Item>
              );
            })}
          </List>
        )}
        <Status
          role="status"
          aria-live="polite"
          $error={Boolean(status?.error)}
        >
          {status?.text || ''}
        </Status>
      </Wrap>
    );
  },
);

MailboxAccessBlock.displayName = 'MailboxAccessBlock';

const Wrap = styled.section`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 12px 0;
  padding: 12px;
  border: 1px solid ${T.border};
  border-radius: 8px;
  background: ${T.surfaceSubtle};
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${T.text};
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${T.textMuted};
`;

/** Как `L.InlineNotice` (страницы) — молекуле страничные токены не видны. */
const Notice = styled.div`
  padding: 8px 10px;
  border: 1px solid ${tagToneColors('danger').bg};
  border-radius: 8px;
  background: ${tagToneColors('danger').bg};
  color: ${tagToneColors('danger').fg};
  font-size: 12px;
  line-height: 1.4;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 16px;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${T.surface};
`;

const Check = styled.label<{ $muted?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  color: ${({ $muted }) => ($muted ? T.textMuted : T.text)};
  overflow-wrap: anywhere;
  cursor: pointer;
`;

/** Как `L.Tag` (страницы): тон из общей палитры меток. */
const Tag = styled.span<{ $tone: 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $tone }) => tagToneColors($tone).bg};
  color: ${({ $tone }) => tagToneColors($tone).fg};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Status = styled.span<{ $error: boolean }>`
  min-height: 16px;
  font-size: 12px;
  color: ${({ $error }) => ($error ? T.danger : T.ok)};
`;
